"use client";

import { toneBadge } from "@/lib/statusTone";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { BellIcon, DismissIcon, PlugIcon } from "@/components/icons";
import { requestJson } from "@/lib/clientRequest";
import { readBusinessCookie } from "@/lib/business/businessCookie";
import { timeAgo } from "@/lib/timeAgo";
import type { ToolRequest } from "@/lib/toolRequests";

/**
 * Tools first, then the plan. While a worker waits for an app its plan is
 * not built, so one closable summary points the person to the inbox. The
 * sidebar bell retains the individual requests. Both read the same list.
 */

const POLL_MS = 15_000;
const CLOSED_KEY = "caw:tool-requests-closed";

interface Ctx {
  items: ToolRequest[];
  loaded: boolean;
  dismissing: ReadonlySet<string>;
  errors: Record<string, string>;
  dismiss: (item: ToolRequest) => Promise<void>;
  refresh: () => void;
}

const ToolRequestsContext = createContext<Ctx>({
  items: [],
  loaded: false,
  dismissing: new Set(),
  errors: {},
  dismiss: async () => {},
  refresh: () => {},
});

export function useToolRequests(): Ctx {
  return useContext(ToolRequestsContext);
}

interface RequestState {
  businessId: string;
  items: ToolRequest[];
  loaded: boolean;
  dismissing: ReadonlySet<string>;
  errors: Record<string, string>;
}

function emptyRequestState(businessId: string): RequestState {
  return { businessId, items: [], loaded: false, dismissing: new Set(), errors: {} };
}

function subscribeBusiness(onChange: () => void) {
  window.addEventListener("caw:activity-refresh", onChange);
  window.addEventListener("focus", onChange);
  document.addEventListener("visibilitychange", onChange);
  return () => {
    window.removeEventListener("caw:activity-refresh", onChange);
    window.removeEventListener("focus", onChange);
    document.removeEventListener("visibilitychange", onChange);
  };
}
const serverBusinessSnapshot = () => "";

/** Reset request state on a business switch without remounting the editor children. */
export function ToolRequestsProvider({ children, businessId }: { children: ReactNode; businessId: string | null }) {
  // The cookie changes before router.refresh supplies the new server prop.
  const cookieBusiness = useSyncExternalStore(subscribeBusiness, readBusinessCookie, serverBusinessSnapshot);
  const scope = cookieBusiness || businessId || "";
  const [state, setState] = useState<RequestState>(() => emptyRequestState(scope));
  const current = state.businessId === scope ? state : emptyRequestState(scope);
  // Separate controller sets keep a late old dismissal from clearing a new one.
  const requests = useMemo(() => ({
    businessId: scope,
    active: true,
    read: null as AbortController | null,
    mutations: new Map<string, AbortController>(),
  }), [scope]);

  const scopeIsCurrent = useCallback(() => {
    const fromCookie = readBusinessCookie();
    return requests.active && (!fromCookie || fromCookie === scope);
  }, [requests, scope]);

  const updateState = useCallback((update: (previous: RequestState) => RequestState) => {
    if (!scopeIsCurrent()) return;
    setState(previous => update(previous.businessId === scope ? previous : emptyRequestState(scope)));
  }, [scope, scopeIsCurrent]);

  const cancelRead = useCallback(() => {
    requests.read?.abort();
    requests.read = null;
  }, [requests]);

  const refresh = useCallback(async () => {
    // Keep pending dismissals visible; a read predating a write must never restore them.
    if (!scope || !scopeIsCurrent() || document.visibilityState !== "visible" || requests.mutations.size) return;
    cancelRead();
    const controller = new AbortController();
    requests.read = controller;
    try {
      const res = await fetch("/api/tool-requests", { signal: controller.signal });
      if (!res.ok) return;
      const data = (await res.json()) as { toolRequests?: ToolRequest[] };
      if (controller.signal.aborted) return;
      updateState(previous => ({ ...previous, items: data.toolRequests ?? [], loaded: true }));
    } catch {
      // Transient — the next visible poll picks it up.
    } finally {
      if (requests.read === controller) requests.read = null;
    }
  }, [scope, scopeIsCurrent, requests, cancelRead, updateState]);

  useEffect(() => {
    requests.active = true;
    const first = setTimeout(refresh, 0);
    const id = setInterval(refresh, POLL_MS);
    const onEvent = () => void refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
      else cancelRead();
    };
    window.addEventListener("caw:activity-refresh", onEvent);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      requests.active = false;
      clearTimeout(first);
      clearInterval(id);
      cancelRead();
      for (const controller of requests.mutations.values()) controller.abort();
      requests.mutations.clear();
      window.removeEventListener("caw:activity-refresh", onEvent);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [requests, refresh, cancelRead]);

  const dismiss = useCallback(async (item: ToolRequest) => {
    if (!scopeIsCurrent() || state.businessId !== scope || !state.items.some(entry => entry.id === item.id && entry.workerId === item.workerId) || requests.mutations.has(item.id)) return;
    cancelRead();
    const controller = new AbortController();
    requests.mutations.set(item.id, controller);
    updateState(previous => {
      const errors = { ...previous.errors };
      delete errors[item.id];
      return { ...previous, dismissing: new Set(requests.mutations.keys()), errors };
    });
    let succeeded = false;
    try {
      await requestJson(`/api/workers/${item.workerId}/resource-requests/${item.id}`, {
        method: "PATCH", body: { dismiss: true }, signal: controller.signal,
        fallback: "Could not dismiss this request. Please try again.",
      });
      if (controller.signal.aborted || !scopeIsCurrent()) return;
      updateState(previous => ({ ...previous, items: previous.items.filter(entry => entry.id !== item.id) }));
      succeeded = true;
    } catch (error) {
      if (!controller.signal.aborted) updateState(previous => ({
        ...previous,
        errors: { ...previous.errors, [item.id]: error instanceof TypeError
          ? "Could not reach the server. Please try again."
          : error instanceof Error ? error.message : "Could not dismiss this request. Please try again." },
      }));
    } finally {
      requests.mutations.delete(item.id);
      if (!controller.signal.aborted && scopeIsCurrent()) {
        updateState(previous => ({ ...previous, dismissing: new Set(requests.mutations.keys()) }));
        if (succeeded) window.dispatchEvent(new CustomEvent("caw:activity-refresh", { detail: { workerId: item.workerId } }));
      }
    }
  }, [scope, state.businessId, state.items, requests, scopeIsCurrent, cancelRead, updateState]);

  return (
    <ToolRequestsContext.Provider value={{ ...current, dismiss, refresh }}>
      {children}
    </ToolRequestsContext.Provider>
  );
}

function readClosed(): Set<string> {
  try {
    const raw = sessionStorage.getItem(CLOSED_KEY);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : []);
  } catch {
    return new Set();
  }
}

function writeClosed(ids: Set<string>) {
  try {
    sessionStorage.setItem(CLOSED_KEY, JSON.stringify([...ids]));
  } catch {
    // Storage may be unavailable; the card simply shows again next load.
  }
}

/** One reminder per batch; closing it leaves the requests in the inbox. */
export function ToolRequestToasts() {
  const { items } = useToolRequests();
  const pathname = usePathname();
  // Items arrive by fetch, so nothing renders on the server and the lazy
  // read of sessionStorage cannot cause a hydration mismatch.
  const [closed, setClosed] = useState<Set<string>>(() =>
    typeof window === "undefined" ? new Set() : readClosed()
  );

  const acknowledge = () => {
    const next = new Set(closed);
    for (const item of items) next.add(item.id);
    writeClosed(next);
    setClosed(next);
  };

  if (pathname === "/monitoring/inbox" || pathname === "/inbox" ||
      !items.some((item) => !closed.has(item.id))) return null;

  return (
    <div className="pointer-events-none absolute right-4 bottom-4 z-40 w-[min(22rem,calc(100%-2rem))]">
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="tool-request-toast"
        className="caw-tool-toast pointer-events-auto rounded-xl border border-zinc-700/80 bg-zinc-900/95 p-3.5 text-zinc-100 shadow-lg shadow-black/20 backdrop-blur"
      >
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg ${toneBadge.attention}`}>
            <BellIcon className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-snug">
              {items.length} connection {items.length === 1 ? "request" : "requests"} waiting
            </p>
            <p className="mt-1 text-xs leading-relaxed text-zinc-400">
              Review the requests in your inbox.
            </p>
            <Link
              href="/monitoring/inbox"
              onClick={acknowledge}
              className="mt-2 inline-flex min-h-8 items-center rounded-md text-xs font-medium text-violet-300 transition hover:text-violet-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-400"
            >
              Open inbox →
            </Link>
          </div>
          <button
            type="button"
            onClick={acknowledge}
            aria-label="Close notification"
            className="-mt-1 -mr-1 flex size-8 shrink-0 items-center justify-center rounded-md text-zinc-400 transition hover:bg-zinc-800 hover:text-white focus-visible:outline-2 focus-visible:outline-violet-400"
          >
            <DismissIcon className="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** The bell in the sidebar: a count, and a tray listing every open request. */
export function ToolRequestBell() {
  const { items, dismissing, errors, dismiss } = useToolRequests();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const count = items.length;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={count > 0 ? `${count} tool request${count === 1 ? "" : "s"}` : "Tool requests"}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Tool requests"
        data-testid="tool-request-bell"
        className={`relative flex size-8 items-center justify-center rounded-lg transition ${
          open ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
        }`}
      >
        <BellIcon className="size-4" />
        {count > 0 && (
          <span className={`caw-tool-badge absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full border px-1 text-[10px] leading-none font-semibold tabular-nums ${toneBadge.attention}`}>
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Tool requests"
          data-testid="tool-request-tray"
          className="caw-tool-tray absolute top-full left-0 z-40 mt-1.5 w-[min(22rem,calc(100vw-2rem))] origin-top-left rounded-xl border border-zinc-800 bg-zinc-900 shadow-xl shadow-black/40"
        >
          <header className="flex items-center justify-between border-b border-zinc-800 px-3.5 py-2.5">
            <h3 className="text-[11px] font-semibold tracking-wide text-zinc-300 uppercase">Waiting on you</h3>
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${count > 0 ? toneBadge.attention : toneBadge.idle}`}>
              {count}
            </span>
          </header>
          {count === 0 ? (
            <p className="px-3.5 py-8 text-center text-xs leading-relaxed text-zinc-500">
              Nothing waiting. When a worker needs an app before its plan can be built, it shows up here.
            </p>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto py-1">
              {items.map((item) => (
                <li key={item.id} className="flex items-start gap-3 px-3.5 py-2.5 transition hover:bg-zinc-800/50">
                  <span className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg ${toneBadge.attention}`}>
                    <PlugIcon className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-snug text-zinc-100">
                      <span className="font-medium">{item.workerName}</span> needs {item.app}
                    </p>
                    <p className="mt-0.5 text-[11px] text-zinc-500">
                      Needs action · Connect this app to prepare the plan · {timeAgo(item.createdAt)}
                    </p>
                    <div className="mt-2 flex items-center gap-1.5">
                      <a
                        href={item.connectUrl}
                        className="inline-flex h-7 items-center rounded-lg bg-blue-600 px-2.5 text-xs font-medium text-white transition hover:bg-blue-500"
                      >
                        Connect
                      </a>
                      <Link
                        href={`/workers/${item.workerId}?tab=resources`}
                        onClick={() => setOpen(false)}
                        className="inline-flex h-7 items-center rounded-lg px-2 text-xs text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-100"
                      >
                        Open worker
                      </Link>
                      <button
                        type="button"
                        onClick={() => void dismiss(item)}
                        disabled={dismissing.has(item.id)}
                        className="ml-auto inline-flex h-7 items-center rounded-lg px-2 text-xs text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200 disabled:cursor-wait disabled:opacity-50"
                      >
                        {dismissing.has(item.id) ? "Dismissing…" : errors[item.id] ? "Retry dismissal" : "Dismiss"}
                      </button>
                    </div>
                    {errors[item.id] && (
                      <p role="alert" className="mt-2 text-xs text-red-300">{errors[item.id]}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
